import { useState } from "react";

import { cn } from "@/lib/cn";
import { useAuthStore } from "@/stores/authStore";

import { BranchesTab } from "./tabs/BranchesTab";
import { BusinessTab } from "./tabs/BusinessTab";
import { DevicesTab } from "./tabs/DevicesTab";
import { ProfileTab } from "./tabs/ProfileTab";
import { UsersTab } from "./tabs/UsersTab";

interface Tab {
  id: string;
  label: string;
  /** Hidden entirely when the user lacks this. Omitted means always shown. */
  permission?: string;
  render: () => React.ReactNode;
}

const TABS: Tab[] = [
  // First, and permission-free: it is the one section here that is about the
  // person rather than the business, so everyone who can open Settings at all
  // can reach it.
  { id: "profile", label: "My profile", render: () => <ProfileTab /> },
  { id: "business", label: "Business", permission: "settings.view", render: () => <BusinessTab /> },
  { id: "branches", label: "Branches & sections", permission: "settings.view", render: () => <BranchesTab /> },
  { id: "users", label: "Users", permission: "users.view", render: () => <UsersTab /> },
  { id: "devices", label: "Tills & devices", permission: "devices.view", render: () => <DevicesTab /> },
];

/**
 * Settings.
 *
 * Tabs are filtered by permission rather than disabled, so a manager without
 * user administration simply does not see that section — a greyed-out tab
 * invites people to ask why, which is a conversation the owner has to have
 * rather than a decision the software already made.
 */
export function SettingsPage() {
  const can = useAuthStore((s) => s.can);
  const visible = TABS.filter((tab) => !tab.permission || can(tab.permission));
  const [active, setActive] = useState(visible[0]?.id);

  if (visible.length === 0) {
    return null; // RequirePermission on the route already explains this case.
  }

  const current = visible.find((tab) => tab.id === active) ?? visible[0]!;

  return (
    <div className="space-y-5">
      <div role="tablist" aria-label="Settings sections" className="flex flex-wrap gap-1 border-b border-line">
        {visible.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === current.id}
            onClick={() => setActive(tab.id)}
            className={cn(
              "-mb-px h-touch border-b-2 px-4 text-sm font-medium transition-colors",
              tab.id === current.id
                ? "border-brand-500 text-ink-900"
                : "border-transparent text-ink-500 hover:text-ink-900",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div role="tabpanel">{current.render()}</div>
    </div>
  );
}
